import React, { useRef } from 'react';
import { Box, Button, Grid, Tooltip, Typography } from '@mui/material';
import html2pdf from 'html2pdf.js';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import AccessAlarmRoundedIcon from '@mui/icons-material/AccessAlarmRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TimerIcon from '@mui/icons-material/Timer';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import GroupsIcon from '@mui/icons-material/Groups';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import FlagIcon from '@mui/icons-material/Flag';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  ImageRun,
  Packer,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';
import { saveAs } from 'file-saver';

const REPORT_WIDTH = 916;
const REPORT_HEIGHT = 1056;
const DASHBOARD_WIDTH = 1280;
const PDF_BOTTOM_MARGIN = 44;
const DEFAULT_LOGO = '/static/vidisky23.png';

const COLORS = {
  ink: '#17171c',
  muted: '#5f6470',
  card: '#ffffff',
  page: '#ffffff',
  section: '#ffffff',
  header: '#f6f7f9',
  grid: '#e7eaf0',
  rowGood: '#f3fbf3',
  rowBad: '#fff4f4',
  rowWarn: '#fff7e6',
  goodText: '#14802c',
  badText: '#b32424',
  warnText: '#9a6b00',
  blue: '#5267f5',
  purple: '#5147e5',
};

const EVENT_ICONS = {
  timer: TimerIcon,
  goal: GpsFixedIcon,
  beer: LocalBarIcon,
  people: GroupsIcon,
  calendar: CalendarMonthIcon,
  pin: LocationOnIcon,
  red: FiberManualRecordIcon,
  target: FlagIcon,
  hourglass: HourglassEmptyIcon,
  book: MenuBookIcon,
  memo: DescriptionIcon,
};

function HeaderEventIcon({ iconKey }) {
  const commonProps = {
    width: 13,
    height: 13,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: '#ffffff',
    strokeWidth: 2.4,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    style: {
      display: 'inline-block',
      marginRight: '8px',
      verticalAlign: '-2px',
      overflow: 'visible',
    },
  };

  if (iconKey === 'goal') {
    return (
      <svg {...commonProps}>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
      </svg>
    );
  }

  if (iconKey === 'beer') {
    return (
      <svg {...commonProps}>
        <path d="M5 4h14l-3 7v8H8v-8L5 4Z" />
        <path d="M8 11h8M10 19h4" />
      </svg>
    );
  }

  if (iconKey === 'people') {
    return (
      <svg {...commonProps}>
        <circle cx="9" cy="8" r="3" />
        <circle cx="16" cy="9" r="2.5" />
        <path d="M3.5 19c.8-3.4 3-5 5.5-5s4.7 1.6 5.5 5" />
        <path d="M13.5 15c2.4.2 4.2 1.6 5 4" />
      </svg>
    );
  }

  return (
    <svg {...commonProps} strokeWidth={2.5}>
      <circle cx="12" cy="13" r="7" />
      <path d="M9 2h6M12 6v7l4 2" />
    </svg>
  );
}

function EventIcon({ iconKey, size = 14, color = 'currentColor', boxSize = 18 }) {
  const Icon = EVENT_ICONS[iconKey];
  if (!Icon) return null;

  return (
    <Box
      component="span"
      style={{
        width: `${boxSize}px`,
        height: `${boxSize}px`,
        minWidth: `${boxSize}px`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        lineHeight: 0,
      }}
    >
      <Icon
        htmlColor={color}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          fontSize: `${size}px`,
          color,
          fill: color,
          display: 'block',
        }}
      />
    </Box>
  );
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const getClientReportDate = () => {
  const date = new Date();
  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();

  return {
    compact: `${MONTHS_SHORT[month]} ${day} ${year}`,
    long: `${MONTHS_LONG[month]} ${day}, ${year}`,
    timestampPrefix: `${day} ${MONTHS_SHORT[month]} ${year}`,
    year,
  };
};

const metricCards = [
  { label: 'Tables Served', value: '241', delta: '+8%', context: 'vs 7-day avg', tone: 'good', color: '#4f67ee', icon: AppsRoundedIcon },
  { label: 'Avg Ticket Time', value: '13:42', delta: '-1:05', context: 'vs 7-day avg', tone: 'good', color: '#09b967', icon: ConfirmationNumberRoundedIcon },
  { label: 'Avg Table Turn', value: '47 min', delta: '-3 min', context: 'vs 7-day avg', tone: 'good', color: '#ff941f', icon: AutorenewRoundedIcon },
  { label: 'Service Alerts', value: '9', delta: '+2', context: 'vs 7-day avg', tone: 'bad', color: '#ec1830', icon: NotificationsActiveRoundedIcon },
];

const speedRows = [
  {
    tone: 'good',
    cells: ['Lunch (11 AM to 2 PM)', '68', '11:58', '12:00', '2:10', '1:21', { text: 'On Target', tone: 'good' }],
  },
  {
    cells: ['Afternoon (2 PM to 5 PM)', '34', '12:47', '14:00', '2:44', '1:35', { text: 'On Target', tone: 'good' }],
  },
  {
    tone: 'bad',
    cells: ['Dinner Peak (5 PM to 8 PM)', '92', '16:21', '14:00', '3:58', '2:49', { text: 'Over Target', tone: 'bad' }],
  },
  {
    cells: ['Late Night (8 PM to 12 AM)', '47', '12:09', '14:00', '2:31', '1:18', { text: 'On Target', tone: 'good' }],
  },
];

const serverRows = [
  { tone: 'good', cells: ['Patio / Marcus T.', '31', '0:48', '3:12', '4%', '1:02', { text: 'Top Performer', tone: 'good' }] },
  { cells: ['Bar Rail / Dana R.', '38', '1:10', '2:58', '6%', '1:21', { text: 'On Target', tone: 'good' }] },
  { cells: ['Main Dining A / Kelsey M.', '42', '1:24', '3:40', '8%', '1:44', { text: 'On Target', tone: 'good' }] },
  { tone: 'bad', cells: ['Main Dining B / Jordan P.', '36', '2:51', '5:22', '17%', '3:08', { text: 'Coaching Flag', tone: 'bad' }] },
  { cells: ['Booths / Alyssa G.', '40', '1:18', '3:25', '7%', '1:30', { text: 'On Target', tone: 'good' }] },
  { tone: 'warn', cells: ['High Tops / Chris B.', '33', '1:42', '4:01', '11%', '2:05', { text: 'Watch', tone: 'warn' }] },
];

const kitchenRows = [
  { tone: 'good', cells: ['Fry Station', '312', '4:05', '4:30', '6 tickets (7:12 PM)', { text: 'On Target', tone: 'good' }] },
  { tone: 'bad', cells: ['Grill', '268', '9:48', '8:00', '11 tickets (6:54 PM)', { text: 'Bottleneck', tone: 'bad' }] },
  { cells: ['Salad / Cold Line', '121', '3:21', '4:00', '3 tickets (12:40 PM)', { text: 'On Target', tone: 'good' }] },
  { cells: ['Expo Window', '489', '1:09 dwell', '1:30', '8 plates (6:58 PM)', { text: 'On Target', tone: 'good' }] },
];

const employeeRows = [
  { cells: ['Avg Server Idle Time', '8.8%', '9.4%', 'Improved, driven by patio and bar rail sections'] },
  { tone: 'good', cells: ['Handwashing Station Compliance (Kitchen)', '96%', '93%', 'Verified at kitchen entry camera per shift change'] },
  { tone: 'bad', cells: ['Off-Station Time, Grill (Peak)', '11 min', '4 min', 'Primary driver of dinner ticket breach'] },
  { tone: 'good', cells: ['Side Work Completion Before Close', '100%', '97%', 'All stations confirmed by closing cameras'] },
  { cells: ['Uniform / Appearance Flags', '0', '0.4', 'No violations observed'] },
];

const revenueRows = [
  {
    cells: [
      '1 preventable walkout (party of 5)',
      '$135 to $165 lost today',
      'Dedicated busser Wed and Thu dinner; host stand table-ready alerts',
    ],
  },
  {
    cells: [
      'Grill bottleneck during dinner peak',
      '2:21 added to avg dinner ticket',
      'Second grill cook 6 to 8 PM; est. 4 to 6 additional table turns per week',
    ],
  },
  {
    cells: ['Table turn improvement (-3 min vs avg)', '~5 added covers today', 'Maintain current expo staffing model'],
  },
];

const eventCards = [
  {
    icon: 'goal',
    title: 'Table Bussing Delay',
    camera: 'GD Main Dining B',
    time: '07:18 PM',
    eventNo: '260611191803_4472',
    videoUrl: 'https://vdointel-ai-storage.s3.us-west-2.amazonaws.com/uploads/vidisky_events/Table_Unclean.mp4',
    rows: [
      { icon: 'calendar', text: (date) => `Date: ${date.long}` },
      { icon: 'pin', text: 'Camera: GD Main Dining B' },
      { icon: 'red', text: 'Disposition: Table Idle Dirty Over 8 Minutes' },
      { icon: 'target', text: 'Action Taken 1: Alerted floor manager via dashboard notification' },
      { icon: 'target', text: 'Action Taken 2: Tracked tables 32 and 34 until reset was completed' },
      { icon: 'hourglass', text: 'Impact: Two 4-top tables sat dirty for 11 minutes with a 6-party wait at the host stand' },
      { icon: 'book', text: 'Agreed Protocol: Escalate any table left unbussed beyond 8 minutes while a wait list is active.' },
      {
        icon: 'memo',
        text:
          'Overall Remarks: Tables 32 and 34 remained unbussed during the dinner wait, an estimated 1 lost turn. Section server was running food for an adjacent section at the time. Recommend a dedicated busser for Main Dining during Wednesday and Thursday dinner peaks.',
      },
    ],
  },
  {
    icon: 'timer',
    title: 'Ticket Time Breach',
    camera: 'GD Kitchen Expo Line',
    time: '06:54 PM',
    eventNo: '260611185402_4471',
    videoUrl: 'https://vdointel-ai-storage.s3.us-west-2.amazonaws.com/uploads/vidisky_events/Guest_Waiting.mp4',
    rows: [
      { icon: 'calendar', text: (date) => `Date: ${date.long}` },
      { icon: 'pin', text: 'Camera: GD Kitchen Expo Line' },
      { icon: 'red', text: 'Disposition: Ticket Time Exceeded 20 Minutes' },
      { icon: 'target', text: 'Action Taken 1: Flagged 11-ticket queue at the grill station to the manager dashboard in real time' },
      { icon: 'target', text: 'Action Taken 2: Monitored expo window live until queue cleared at 7:21 PM' },
      { icon: 'hourglass', text: 'Peak Delay: Table 41 ticket reached 23:40 against a 14:00 dinner target' },
      {
        icon: 'book',
        text: 'Agreed Protocol: Escalate any ticket exceeding target time by more than 5 minutes during peak service.',
      },
      {
        icon: 'memo',
        text:
          'Overall Remarks: Grill station fell behind during the 6:30 to 7:00 PM push with 11 tickets in queue. One cook was off station for approximately 6 minutes during the buildup. Expo dwell remained on target, confirming delay originated at the grill. Recommend a second grill cook scheduled Thursdays 6:00 to 8:00 PM.',
      },
    ],
  },
  {
    icon: 'beer',
    title: 'Bar Service Delay',
    camera: 'GD Bar Well 1',
    time: '08:02 PM',
    eventNo: '260611200214_4473',
    videoUrl: 'https://vdointel-ai-storage.s3.us-west-2.amazonaws.com/uploads/vidisky_events/Guest_Waiting.mp4',
    rows: [
      { icon: 'calendar', text: (date) => `Date: ${date.long}` },
      { icon: 'pin', text: 'Camera: GD Bar Well 1' },
      { icon: 'red', text: 'Disposition: Guest Unacknowledged Over 3 Minutes' },
      { icon: 'target', text: 'Action Taken 1: Flagged unattended guests at bar seats 7 through 9 to the manager dashboard' },
      { icon: 'target', text: 'Action Taken 2: Monitored live until guests were greeted at 8:06 PM' },
      { icon: 'book', text: 'Agreed Protocol: Escalate any seated bar guest not greeted within 90 seconds.' },
      {
        icon: 'memo',
        text:
          'Overall Remarks: Three guests seated at the bar went 4:12 without acknowledgment while both bartenders worked the service well for dining room drink tickets. Service well volume from 7:45 to 8:15 PM was 38 drinks. Recommend reviewing whether dinner peak drink tickets should split between both wells.',
      },
    ],
  },
  {
    icon: 'people',
    title: 'Host Stand / Wait Time',
    camera: 'GD Front Entry',
    time: '06:41 PM',
    eventNo: '260611184109_4474',
    videoUrl: 'https://vdointel-ai-storage.s3.us-west-2.amazonaws.com/uploads/vidisky_events/Table_Unclean.mp4',
    rows: [
      { icon: 'calendar', text: (date) => `Date: ${date.long}` },
      { icon: 'pin', text: 'Camera: GD Front Entry' },
      { icon: 'red', text: 'Disposition: Walkout Risk, Party Departed During Wait' },
      { icon: 'target', text: 'Action Taken 1: Logged party of 5 leaving after 14 minutes unseated' },
      { icon: 'target', text: 'Action Taken 2: Cross referenced dining floor cameras showing 3 open clean tables at time of departure' },
      { icon: 'book', text: 'Agreed Protocol: Escalate any guest party departing before seating, including table availability context.' },
      {
        icon: 'memo',
        text:
          'Overall Remarks: A party of 5 left at 6:41 PM after waiting 14 minutes. Floor cameras confirmed tables 32 and 34 were dirty (see bussing event) and table 18 was clean but unassigned. Estimated lost revenue of $135 to $165 based on average dinner party check. This walkout was preventable with faster table resets.',
      },
    ],
  },
];

const rowBackground = (tone) => {
  if (tone === 'good') return COLORS.rowGood;
  if (tone === 'bad') return COLORS.rowBad;
  if (tone === 'warn') return COLORS.rowWarn;
  return '#ffffff';
};

const badgeStyles = (tone) => {
  if (tone === 'bad') return { backgroundColor: '#fdeeee', color: COLORS.badText };
  if (tone === 'warn') return { backgroundColor: '#fff7e6', color: COLORS.warnText };
  return { backgroundColor: '#e7f7e7', color: COLORS.goodText };
};

function Section({ title, icon: Icon, children, sx = {} }) {
  return (
    <Box
      className="pdf-section dashboard-card"
      sx={{
        border: `1px solid ${COLORS.grid}`,
        borderRadius: '8px',
        p: '13px',
        boxShadow: '0 1px 3px rgba(30, 41, 59, 0.04)',
        backgroundColor: COLORS.section,
        boxSizing: 'border-box',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', minHeight: '22px', mb: '7px' }}>
        {Icon ? <Icon sx={{ color: COLORS.purple, fontSize: '20px' }} /> : null}
        <Typography sx={{ fontSize: '13px', lineHeight: '16px', fontWeight: 800, color: COLORS.purple, textTransform: 'uppercase' }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

function Metrics() {
  return (
    <Box
      className="metrics-grid"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: '10px',
        mb: '10px',
      }}
    >
      {metricCards.map((card) => {
        const MetricIcon = card.icon;
        const TrendIcon = card.tone === 'bad' ? TrendingUpRoundedIcon : TrendingDownRoundedIcon;

        return (
          <Box
            key={card.label}
            sx={{
              height: '88px',
              border: `1px solid ${COLORS.grid}`,
              borderRadius: '8px',
              backgroundColor: COLORS.card,
              color: COLORS.ink,
              boxSizing: 'border-box',
              px: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '13px',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(30, 41, 59, 0.05)',
            }}
          >
            <Box sx={{ width: '48px', height: '48px', borderRadius: '50%', bgcolor: card.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <MetricIcon sx={{ fontSize: '27px' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '11.5px', lineHeight: '14px', fontWeight: 700 }}>{card.label}</Typography>
              <Typography sx={{ fontSize: '22px', lineHeight: '25px', fontWeight: 800, mt: '1px' }}>{card.value}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', whiteSpace: 'nowrap' }}>
                <TrendIcon sx={{ fontSize: '13px', color: card.tone === 'bad' ? '#ed263d' : '#08ad60' }} />
                <Typography component="span" sx={{ fontSize: '9.5px', lineHeight: '12px', fontWeight: 800, color: card.tone === 'bad' ? '#ed263d' : '#08ad60' }}>{card.delta}</Typography>
                <Typography component="span" sx={{ fontSize: '9px', lineHeight: '12px', color: COLORS.muted }}>{card.context}</Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function DataTable({ columns, rows, colWidths }) {
  const totalWidth = colWidths.reduce((sum, width) => sum + width, 0);

  return (
    <Box className="table-wrap" sx={{ overflow: 'hidden', borderRadius: '5px' }}>
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          fontFamily: '"Liberation Sans", Arial, sans-serif',
        }}
      >
        <colgroup>
          {colWidths.map((width, index) => (
            <col key={`${width}-${index}`} style={{ width: `${(width / totalWidth) * 100}%` }} />
          ))}
        </colgroup>
        <Box component="thead">
          <Box component="tr">
            {columns.map((column, index) => (
              <Box
                component="th"
                key={column}
                sx={{
                  height: '34px',
                  border: `1px solid ${COLORS.grid}`,
                  backgroundColor: COLORS.header,
                  color: COLORS.ink,
                  fontSize: '9.5px',
                  lineHeight: '12px',
                  fontWeight: 700,
                  px: '7px',
                  textAlign: index === 0 ? 'left' : 'center',
                  verticalAlign: 'middle',
                  whiteSpace: 'normal',
                  //paddingTop: '6px',
                }}
              >
                {column}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((row, rowIndex) => (
            <Box component="tr" key={`${row.cells[0]}-${rowIndex}`}>
              {row.cells.map((cell, cellIndex) => {
                const isBadge = typeof cell === 'object';
                const text = isBadge ? cell.text : cell;
                const cellTone = isBadge ? cell.tone : row.tone;

                return (
                  <Box
                    component="td"
                    key={`${text}-${cellIndex}`}
                    sx={{
                      height: '34px',
                      border: `1px solid ${COLORS.grid}`,
                      backgroundColor: rowBackground(row.tone),
                      color: COLORS.ink,
                      fontSize: '9.5px',
                      lineHeight: '13px',
                      fontWeight: 400,
                      px: '7px',
                      textAlign: cellIndex === 0 ? 'left' : 'center',
                      verticalAlign: 'middle',
                      whiteSpace: 'normal',
                    }}
                  >
                    {isBadge ? (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          borderRadius: '9px',
                          px: '7px',
                          py: '4px',
                          minWidth: '54px',
                          fontSize: '8.5px',
                          lineHeight: '11px',
                          fontWeight: 700,
                          ...badgeStyles(cellTone),
                        }}
                      >
                        {text}
                      </Box>
                    ) : (
                      text
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function EventCard({ event, date }) {
  return (
    <Box
      className="event-card"
      sx={{
        backgroundColor: COLORS.section,
        borderRadius: '16px',
        boxSizing: 'border-box',
        px: '20px',
        pt: '13px',
        pb: '18px',
        mb: '16px',
      }}
    >
      <span
        style={{
          minHeight: '30px',
          borderRadius: '15px',
          backgroundColor: '#111111',
          color: '#ffffff',
          display: 'inline-block',
          padding: '8px 16px',
          fontSize: '11.5px',
          lineHeight: '14px',
          fontWeight: 700,
          whiteSpace: 'nowrap',
          marginBottom: '18px',
          WebkitTextFillColor: '#ffffff',
          boxSizing: 'border-box',
        }}
      >
        <HeaderEventIcon iconKey={event.icon} />
        {event.title}
      </span>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '18px', mb: '14px' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '13px', lineHeight: '15px', fontWeight: 700, color: COLORS.ink }}>
            {event.camera}
          </Typography>
          <Typography sx={{ fontSize: '11px', lineHeight: '13px', color: COLORS.blue, mt: '3px' }}>
            {date.timestampPrefix} {event.time}
          </Typography>
          <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', color: '#333333', mt: '3px' }}>
            Event No: {event.eventNo}
          </Typography>
        </Box>

        <Box
           component="a"
            href={event.videoUrl}   
            target="_blank"
          sx={{
            width: '110px',
            height: '34px',
            mt: '8px',
            borderRadius: '6px',
            backgroundColor: COLORS.blue,
            color: '#ffffff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: '10.5px',
            lineHeight: '12px',
            fontWeight: 700,
            textDecoration: 'none',
            '&:link, &:visited, &:hover, &:active, &:focus': {
              color: '#ffffff',
              textDecoration: 'none',
            },
          }}
        >
          View Video
        </Box>
      </Box>

      <Box sx={{ color: COLORS.ink }}>
        {event.rows.map((row, index) => {
          const text = typeof row.text === 'function' ? row.text(date) : row.text;

          return (
            <Box
              key={`${event.title}-${index}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: '18px 1fr',
                columnGap: '5px',
                alignItems: 'start',
                fontSize: '10.5px',
                lineHeight: '16px',
                minHeight: '16px',
                mb: '5px',
              }}
            >
              <EventIcon
                iconKey={row.icon}
                color={row.icon === 'red' ? '#ff0000' : COLORS.ink}
                size={row.icon === 'red' ? 7 : 18}
                boxSize={16}
              />
               <Box 
    component="span" 
    sx={{ 
      minWidth: 0, 
      lineHeight: '14px',  // Reduced from 16px to move text up
      pt: '0px',
      mt: '-2px',  // Negative margin to pull text upward
      display: 'block'
    }}
  >
    {text}
  </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

const wordTableBorders = {
  top: { style: BorderStyle.SINGLE, size: 1, color: 'D9DDE5' },
  bottom: { style: BorderStyle.SINGLE, size: 1, color: 'D9DDE5' },
  left: { style: BorderStyle.SINGLE, size: 1, color: 'D9DDE5' },
  right: { style: BorderStyle.SINGLE, size: 1, color: 'D9DDE5' },
  insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: 'D9DDE5' },
  insideVertical: { style: BorderStyle.SINGLE, size: 1, color: 'D9DDE5' },
};

const wordToneFill = (tone) => {
  if (tone === 'good') return 'F3FBF3';
  if (tone === 'bad') return 'FFF4F4';
  if (tone === 'warn') return 'FFF7E6';
  return 'FFFFFF';
};

function exteriorWordCell(value, options = {}) {
  const text = typeof value === 'object' ? value.text : value;
  const tone = typeof value === 'object' ? value.tone : options.tone;
  const color = tone === 'bad' ? 'B32424' : tone === 'warn' ? '9A6B00' : tone === 'good' ? '14802C' : '1A1A1E';

  return new TableCell({
    shading: { type: ShadingType.CLEAR, fill: options.fill || wordToneFill(options.rowTone) },
    children: [
      new Paragraph({
        alignment: options.align || AlignmentType.CENTER,
        spacing: { before: 45, after: 45 },
        children: [new TextRun({ text: String(text), bold: options.bold || typeof value === 'object', color, size: options.size || 17 })],
      }),
    ],
  });
}

function exteriorWordTable(columns, rows) {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: wordTableBorders,
    rows: [
      new TableRow({
        tableHeader: true,
        children: columns.map((column, index) => exteriorWordCell(column, {
          bold: true,
          fill: 'F2F2F4',
          align: index === 0 ? AlignmentType.LEFT : AlignmentType.CENTER,
        })),
      }),
      ...rows.map((row) => new TableRow({
        cantSplit: true,
        children: row.cells.map((cell, index) => exteriorWordCell(cell, {
          rowTone: row.tone,
          align: index === 0 ? AlignmentType.LEFT : AlignmentType.CENTER,
        })),
      })),
    ],
  });
}

function exteriorWordHeading(text) {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 220, after: 90 },
    children: [new TextRun({ text, bold: true, color: '1A1A1E', size: 22 })],
  });
}

function exteriorEventTable(event, reportDate) {
  const detailParagraphs = event.rows.map((row) => {
    const text = typeof row.text === 'function' ? row.text(reportDate) : row.text;
    return new Paragraph({
      spacing: { after: 55 },
      children: [new TextRun({ text: `• ${text}`, color: '1A1A1E', size: 18 })],
    });
  });

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: wordTableBorders,
    rows: [
      new TableRow({
        cantSplit: true,
        children: [
          new TableCell({
            shading: { type: ShadingType.CLEAR, fill: 'F1F1F3' },
            margins: { top: 130, right: 160, bottom: 130, left: 160 },
            children: [
              new Paragraph({
                spacing: { after: 75 },
                children: [new TextRun({ text: event.title, bold: true, color: 'FFFFFF', shading: { type: ShadingType.CLEAR, fill: '111111' }, size: 20 })],
              }),
              new Paragraph({ children: [new TextRun({ text: event.camera, bold: true, size: 20 })] }),
              new Paragraph({
                spacing: { after: 75 },
                children: [new TextRun({ text: `${reportDate.timestampPrefix} ${event.time}  |  Event No: ${event.eventNo}`, color: '5B6CFF', size: 17 })],
              }),
              ...detailParagraphs,
              new Paragraph({ children: [new TextRun({ text: `Video: ${event.videoUrl}`, color: '5B6CFF', size: 16 })] }),
            ],
          }),
        ],
      }),
    ],
  });
}

async function getExteriorLogo() {
  try {
    const response = await fetch(DEFAULT_LOGO);
    if (!response.ok) return null;
    return new Uint8Array(await response.arrayBuffer());
  } catch (error) {
    console.error('Unable to add the report logo to the Word document:', error);
    return null;
  }
}

export default function DailyEscalationTemplateNew({ fileName }) {
  const pdfRef = useRef();
  const reportDate = getClientReportDate();
  const exportBaseName = (fileName || 'VIDISKY_GatorsDockside_DailyOps').replace(/\.(pdf|docx)$/i, '');

  const waitForImages = async (element) => {
    const images = Array.from(element.querySelectorAll('img'));
    await Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete) {
              resolve();
              return;
            }
            img.onload = () => resolve();
            img.onerror = () => resolve();
          })
      )
    );
  };

  const downloadPDF = async () => {
    const element = pdfRef.current;
    if (!element) return;

    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch (error) {
        console.error('Font readiness check failed:', error);
      }
    }

    await waitForImages(element);

    const exportContainer = document.createElement('div');
    const clonedElement = element.cloneNode(true);

    exportContainer.style.position = 'fixed';
    exportContainer.style.top = '0';
    exportContainer.style.left = '-10000px';
    exportContainer.style.width = `${REPORT_WIDTH}px`;
    exportContainer.style.margin = '0';
    exportContainer.style.padding = '0';
    exportContainer.style.backgroundColor = '#ffffff';
    exportContainer.style.pointerEvents = 'none';
    exportContainer.style.zIndex = '-1';

    clonedElement.style.width = `${REPORT_WIDTH}px`;
    clonedElement.style.margin = '0';
    clonedElement.style.boxShadow = 'none';
    exportContainer.appendChild(clonedElement);
    document.body.appendChild(exportContainer);

    try {
      await waitForImages(clonedElement);

      await html2pdf()
        .from(clonedElement)
        .set({
          margin: [0, 0, PDF_BOTTOM_MARGIN, 0],
          filename: fileName || 'VIDISKY_GatorsDockside_DailyOps.pdf',
          enableLinks: true,
          image: { type: 'png', quality: 1 },
          html2canvas: {
            scale: 4,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            scrollX: 0,
            scrollY: 0,
            windowWidth: REPORT_WIDTH,
            width: REPORT_WIDTH,
          },
          jsPDF: {
            unit: 'px',
            format: [REPORT_WIDTH, REPORT_HEIGHT],
            orientation: 'portrait',
            hotfixes: ['px_scaling'],
          },
          pagebreak: {
            mode: ['css', 'legacy'],
            avoid: ['.event-card', '.table-wrap', 'tr'],
          },
        })
        .save();
    } finally {
      document.body.removeChild(exportContainer);
    }
  };

  const downloadWord = async () => {
    const logoData = await getExteriorLogo();
    const metricsTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: wordTableBorders,
      rows: [
        new TableRow({
          cantSplit: true,
          children: metricCards.map((card) => new TableCell({
            shading: { type: ShadingType.CLEAR, fill: '1C1C1E' },
            margins: { top: 90, right: 70, bottom: 90, left: 70 },
            children: [
              new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: card.label, bold: true, color: 'FFFFFF', size: 18 })] }),
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { before: 50, after: 35 }, children: [new TextRun({ text: card.value, bold: true, color: 'FFFFFF', size: 28 })] }),
              new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: `${card.delta} ${card.context}`, bold: true, color: card.tone === 'bad' ? 'FF9C9C' : '9BE29B', size: 16 })] }),
            ],
          })),
        }),
      ],
    });

    const documentChildren = [
      ...(logoData ? [new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 80 },
        children: [new ImageRun({ data: logoData, transformation: { width: 150, height: 48 }, type: 'png' })],
      })] : []),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 50 },
        children: [new TextRun({ text: `Daily Operations Report: ${reportDate.compact}`, bold: true, color: '1A1A1E', size: 28 })],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 35 },
        children: [new TextRun({ text: 'Gators Dockside', bold: true, color: '1A1A1E', size: 23 })],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 180 },
        children: [new TextRun({ text: 'Operating Hours Analyzed: 11:00 AM to 12:00 AM  |  Cameras Active: 12 of 12  |  Verified by VIDISKY Command Centre', color: '555555', size: 17 })],
      }),
      metricsTable,
      exteriorWordHeading('Speed of Service by Daypart'),
      exteriorWordTable(['Daypart', 'Orders', 'Avg Ticket Time', 'Target', 'Greet to Order', 'Food to Table', 'Status'], speedRows),
      exteriorWordHeading('Server Section Performance'),
      exteriorWordTable(['Section / Server', 'Tables', 'Avg Greet Time', 'Avg Check Drop', 'Idle Time %', 'Refill Response', 'Rating'], serverRows),
      exteriorWordHeading('Kitchen Throughput'),
      exteriorWordTable(['Station', 'Items Fired', 'Avg Prep Time', 'Target', 'Longest Queue', 'Status'], kitchenRows),
      exteriorWordHeading('Escalated Operational Events'),
      ...eventCards.flatMap((event) => [
        exteriorEventTable(event, reportDate),
        new Paragraph({ spacing: { after: 100 } }),
      ]),
      exteriorWordHeading('Employee Activity Summary'),
      exteriorWordTable(['Metric', 'Today', '7-Day Avg', 'Notes'], employeeRows),
      exteriorWordHeading('Revenue Impact Summary'),
      exteriorWordTable(['Finding', 'Estimated Impact', 'Recommendation'], revenueRows),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 180 },
        children: [new TextRun({ text: `All Rights Reserved ©${reportDate.year} by VIDISKY`, color: '555555', size: 16 })],
      }),
    ];

    const wordDocument = new Document({
      sections: [{
        properties: { page: { margin: { top: 500, right: 500, bottom: 500, left: 500 } } },
        children: documentChildren,
      }],
    });

    const blob = await Packer.toBlob(wordDocument);
    saveAs(blob, `${exportBaseName}.docx`);
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} mt={2} sx={{ display: 'flex', justifyContent: 'flex-end', pr: 2 }}>
          <Tooltip title="Download PDF" arrow>
            <Button
              id="daily-escalation-download-trigger"
              aria-label="Download PDF"
              onClick={downloadPDF}
              variant="contained"
              sx={{ minWidth: 34, width: 34, height: 32, p: 0, bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
            >
              <PictureAsPdfRoundedIcon sx={{ fontSize: 19 }} />
            </Button>
          </Tooltip>
          <Tooltip title="Download Word" arrow>
            <Button
              id="daily-escalation-download-trigger-word"
              aria-label="Download Word"
              onClick={downloadWord}
              variant="contained"
              sx={{ ml: 1.25, minWidth: 34, width: 34, height: 32, p: 0, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
            >
              <DescriptionRoundedIcon sx={{ fontSize: 19 }} />
            </Button>
          </Tooltip>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#f5f5f5', p: 2, overflowX: 'auto' }}>
        <Box
          ref={pdfRef}
          sx={{
            width: '100%',
            maxWidth: `${DASHBOARD_WIDTH}px`,
            backgroundColor: COLORS.page,
            color: COLORS.ink,
            fontFamily: '"Liberation Sans", Arial, sans-serif',
            fontSize: '11px',
            lineHeight: 1.35,
            boxSizing: 'border-box',
            px: '34px',
            //pt: '4px',
            pb: '28px',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
          }}
        >
          <style>
            {`
              @page { size: 816px 1056px; margin: 0; }
              .event-card,
              .table-wrap {
                break-inside: avoid;
                page-break-inside: avoid;
              }
              tr {
                break-inside: avoid;
                page-break-inside: avoid;
              }
            `}
          </style>

          <Box sx={{ textAlign: 'center', mt: '-30px',}}>
            <Box
              component="img"
              src={DEFAULT_LOGO}
              alt="VIDISKY AI"
              sx={{   width: '200px',   // Increased width
    height: 'auto',   // Maintain aspect ratio
    objectFit: 'contain'}}
            />
          </Box>

          <Box sx={{ mb: '14px', mt: '-40px'  }}>
            <Typography sx={{ fontSize: '15px', lineHeight: '18px', fontWeight: 700, color: COLORS.ink }}>
              Daily Operations Report: {reportDate.compact}
            </Typography>
            <Typography sx={{ fontSize: '13px', lineHeight: '15px', fontWeight: 700, mt: '3px', color: COLORS.ink }}>
              Gators Dockside
            </Typography>
            <Typography sx={{ fontSize: '11px', lineHeight: '13px', color: COLORS.muted, mt: '2px' }}>
              Operating Hours Analyzed: 11:00 AM to 12:00 AM&nbsp; | &nbsp;Cameras Active: 12 of 12&nbsp; | &nbsp;Verified by VIDISKY Command Centre
            </Typography>
          </Box>

          <Metrics />

          <Box
            className="dashboard-grid"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: '10px',
              alignItems: 'start',
              mb: '16px',
            }}
          >
            <Box sx={{ display: 'grid', gap: '10px' }}>
              <Section
                title="Speed of Service by Daypart"
                icon={AccessAlarmRoundedIcon}
                sx={{ '& th, & td': { height: '43px' } }}
              >
                <DataTable
                  columns={['Daypart', 'Orders', 'Avg Ticket Time', 'Target', 'Greet to Order', 'Food to Table', 'Status']}
                  colWidths={[177, 63, 116, 59, 107, 102, 104]}
                  rows={speedRows}
                />
              </Section>

              <Section title="Kitchen Throughput" icon={RestaurantRoundedIcon}>
                <DataTable
                  columns={['Station', 'Items Fired', 'Avg Prep Time', 'Target', 'Longest Queue', 'Status']}
                  colWidths={[141, 106, 130, 72, 159, 120]}
                  rows={kitchenRows}
                />
              </Section>

              <Section title="Revenue Impact Summary" icon={AttachMoneyRoundedIcon}>
                <DataTable
                  columns={['Finding', 'Estimated Impact', 'Recommendation']}
                  colWidths={[207, 167, 354]}
                  rows={revenueRows}
                />
              </Section>
            </Box>

            <Box sx={{ display: 'grid', gap: '10px' }}>
              <Section title="Server Section Performance" icon={PersonRoundedIcon}>
                <DataTable
                  columns={['Section / Server', 'Tables', 'Avg Greet Time', 'Avg Check Drop', 'Idle Time %', 'Refill Response', 'Rating']}
                  colWidths={[152, 57, 106, 110, 84, 108, 111]}
                  rows={serverRows}
                />
              </Section>

              <Section title="Employee Activity Summary" icon={BadgeRoundedIcon}>
                <DataTable
                  columns={['Metric', 'Today', '7-Day Avg', 'Operational Notes']}
                  colWidths={[276, 62, 86, 304]}
                  rows={employeeRows}
                />
              </Section>
            </Box>
          </Box>

          <Section title="Escalated Operational Events" icon={NotificationsActiveRoundedIcon} sx={{ mb: '10px' }}>
            {eventCards.map((event) => (
              <EventCard key={event.title} event={event} date={reportDate} />
            ))}
          </Section>

          <Typography sx={{ textAlign: 'center', fontSize: '10.5px', lineHeight: '13px', color: COLORS.ink, mt: '6px' }}>
            {`All Rights Reserved \u00a9${reportDate.year} by VIDISKY`}
          </Typography>
        </Box>
      </Box>
    </>
  );
}
